import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { 
  FileText, Upload, Save, Image, Type, Code,
  Check, AlertCircle, Loader2
} from 'lucide-react';
import AdminLayout from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminContent = () => {
  const [legalPages, setLegalPages] = useState([]);
  const [selectedLegal, setSelectedLegal] = useState('mentions-legales');
  const [legalContent, setLegalContent] = useState({ title: '', content: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadedImages, setUploadedImages] = useState([]);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchLegalPages();
    fetchUploadedImages();
  }, []);

  useEffect(() => {
    fetchLegalPage(selectedLegal);
  }, [selectedLegal]);

  const fetchLegalPages = async () => {
    try {
      const res = await axios.get(`${API}/legal-pages`);
      setLegalPages(res.data);
    } catch (error) {
      console.error('Error fetching legal pages:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLegalPage = async (slug) => {
    try {
      const res = await axios.get(`${API}/legal-pages/${slug}`);
      setLegalContent({ title: res.data.title || '', content: res.data.content || '' });
    } catch (error) {
      console.error('Error fetching legal page:', error);
      setLegalContent({ title: '', content: '' });
    }
  };

  const fetchUploadedImages = async () => {
    // For now, we don't have a list endpoint, but images are stored
  };

  const saveLegalPage = async () => {
    setSaving(true);
    const token = localStorage.getItem('jardily_token');
    try {
      await axios.put(`${API}/legal-pages/${selectedLegal}`, legalContent, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Page légale sauvegardée !');
      fetchLegalPages();
    } catch (error) {
      console.error('Error saving legal page:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const token = localStorage.getItem('jardily_token');
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await axios.post(`${API}/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      const imageUrl = `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
      setUploadedImages(prev => [...prev, { filename: res.data.filename, url: imageUrl }]);
      
      // Copy URL to clipboard
      navigator.clipboard.writeText(imageUrl);
      toast.success('Image uploadée ! URL copiée dans le presse-papier.');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'upload');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const legalPageNames = {
    'mentions-legales': 'Mentions légales',
    'politique-confidentialite': 'Politique de confidentialité',
    'cgv': 'Conditions Générales de Vente'
  };

  return (
    <AdminLayout>
      <div className="p-6 lg:p-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading font-bold text-2xl text-stone-900">Gestion du contenu</h1>
          <p className="text-stone-600 text-sm mt-1">
            Modifiez les textes et images de votre site
          </p>
        </div>

        <Tabs defaultValue="legal" className="space-y-6">
          <TabsList className="bg-stone-100 p-1 rounded-xl">
            <TabsTrigger value="legal" className="rounded-lg data-[state=active]:bg-white">
              <FileText className="w-4 h-4 mr-2" />
              Pages légales
            </TabsTrigger>
            <TabsTrigger value="images" className="rounded-lg data-[state=active]:bg-white">
              <Image className="w-4 h-4 mr-2" />
              Images
            </TabsTrigger>
          </TabsList>

          {/* Legal Pages Tab */}
          <TabsContent value="legal" className="space-y-6">
            <div className="bg-white rounded-2xl border border-stone-200 p-6">
              <div className="flex flex-wrap gap-2 mb-6">
                {Object.entries(legalPageNames).map(([slug, name]) => (
                  <button
                    key={slug}
                    onClick={() => setSelectedLegal(slug)}
                    className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                      selectedLegal === slug
                        ? 'bg-emerald-600 text-white'
                        : 'bg-stone-100 text-stone-700 hover:bg-stone-200'
                    }`}
                  >
                    {name}
                  </button>
                ))}
              </div>

              <div className="space-y-4">
                <div>
                  <Label>Titre de la page</Label>
                  <Input
                    value={legalContent.title}
                    onChange={(e) => setLegalContent({ ...legalContent, title: e.target.value })}
                    placeholder="Titre de la page"
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label>Contenu (HTML)</Label>
                  <Textarea
                    value={legalContent.content}
                    onChange={(e) => setLegalContent({ ...legalContent, content: e.target.value })}
                    placeholder="<h2>Titre</h2><p>Contenu...</p>"
                    rows={20}
                    className="mt-1 font-mono text-sm"
                  />
                </div>

                <div className="flex justify-end">
                  <Button
                    onClick={saveLegalPage}
                    disabled={saving}
                    className="btn-primary"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Sauvegarde...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Sauvegarder
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Images Tab */}
          <TabsContent value="images" className="space-y-6">
            <div className="bg-white rounded-2xl border border-stone-200 p-6">
              <h3 className="font-semibold text-stone-900 mb-4">Uploader une image</h3>
              
              <div className="border-2 border-dashed border-stone-300 rounded-xl p-8 text-center">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  id="image-upload"
                />
                <label
                  htmlFor="image-upload"
                  className="cursor-pointer flex flex-col items-center"
                >
                  {uploading ? (
                    <Loader2 className="w-12 h-12 text-emerald-600 animate-spin mb-4" />
                  ) : (
                    <Upload className="w-12 h-12 text-stone-400 mb-4" />
                  )}
                  <span className="text-stone-700 font-medium">
                    {uploading ? 'Upload en cours...' : 'Cliquez pour sélectionner une image'}
                  </span>
                  <span className="text-stone-500 text-sm mt-1">
                    JPG, PNG, WEBP ou GIF (max 10MB)
                  </span>
                </label>
              </div>

              {uploadedImages.length > 0 && (
                <div className="mt-6">
                  <h4 className="font-medium text-stone-900 mb-3">Images uploadées cette session</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {uploadedImages.map((img, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={img.url}
                          alt={img.filename}
                          className="w-full h-32 object-cover rounded-xl"
                        />
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(img.url);
                            toast.success('URL copiée !');
                          }}
                          className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center"
                        >
                          <span className="text-white text-sm font-medium">Copier l'URL</span>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-6 p-4 bg-amber-50 rounded-xl">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-800">
                    <p className="font-medium mb-1">Comment utiliser les images</p>
                    <p>Après l'upload, copiez l'URL de l'image et collez-la dans les champs "image" des services, articles de blog ou avis clients depuis leurs pages de gestion respectives.</p>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
};

export default AdminContent;
